(function () {
  if (window.FoxTheme?.settings?.promoUrlActivationEnabled === false) return;

  const STORAGE_CODE = "gloura:promo-code";
  const STORAGE_MINIMIZED = "gloura:promo-minimized";
  const STORAGE_REDIRECT = "gloura:promo-redirect";
  const STORAGE_PROBE_DONE_PREFIX = "gloura:promo-probe-done:";
  const STORAGE_VALIDATION_PREFIX = "gloura:promo-validation:";
  const STORAGE_USER_OVERRIDE = "gloura:promo-user-override";
  const STORAGE_LAST_CART_CODES = "gloura:promo-last-cart-codes";
  const STORAGE_URL_SEEN_ON_CART = "gloura:promo-url-seen-on-cart";
  const STORAGE_MANUAL_AUTO_MINIMIZED = "gloura:promo-manual-auto-minimized";
  const STORAGE_INLINE_SEEN_PREFIX = "gloura:promo-inline-seen:";
  const STORAGE_FORCE_INLINE = "gloura:promo-force-inline";
  const STORAGE_PERSISTED_PROMO = "gloura:promo-persisted";
  const STORAGE_PROBE_VARIANT = "gloura:promo-probe-variant";
  const PERSISTED_PROMO_TTL_MS = 48 * 60 * 60 * 1000;
  const PROBE_LINE_PROPERTY = "_gloura_discount_probe";
  const INLINE_SUCCESS_DISMISS_MS = 4200;
  const CAMPAIGN_DEFAULT_CODE = "GLOURA";
  const EXPLICIT_DISCOUNT_ACTION_MS = 2500;
  const GLOURA_REASSERT_DELAY_MS = 4000;
  const CHECKOUT_OWNED_CODE_TIMEOUT_MS = 10000;
  const CHECKOUT_INTENT_STATE_ATTR = "data-gloura-checkout-state";

  const root = document.querySelector("gloura-promo-activation");
  if (!root) return;

  installDiscountFetchGuard();

  const inlineEl = root.querySelector("[data-promo-inline]");
  const inlineMessageEl = root.querySelector("[data-promo-inline-message]");
  const inlineDismissEl = root.querySelector("[data-promo-inline-dismiss]");
  const triggerEl = root.querySelector("[data-promo-trigger]");
  const triggerLabelEl = root.querySelector("[data-promo-trigger-label]");
  const popoverEl = root.querySelector("[data-promo-popover]");
  const messageEl = root.querySelector("[data-promo-message]");
  const messageBodyEl = root.querySelector("[data-promo-message-body]");
  const messageMetaEl = root.querySelector("[data-promo-message-meta]");
  const breakdownEl = root.querySelector("[data-promo-breakdown]");
  const totalEl = root.querySelector("[data-promo-total]");
  const totalLabelEl = root.querySelector("[data-promo-total-label]");
  const totalValueEl = root.querySelector("[data-promo-total-value]");
  const popoverDismissEl = root.querySelector("[data-promo-popover-dismiss]");
  const strings = window.FoxTheme?.promoActivation?.strings || {};

  let syncInFlight = false;
  let campaignDefaultEnsureInFlight = false;
  let lastCampaignDefaultEnsureKey = "";
  let probeInFlight = false;
  let probeScrubInFlight = false;
  let announcementRemountTimer = null;
  let announcementObserver = null;
  let currentInlineState = null;
  let currentInlineCode = null;
  let inlineSuccessTimer = null;
  const probeCompletedCodes = new Set();
  const preferredCodeAttempts = new Set();
  let explicitDiscountActionPending = false;
  let explicitDiscountActionTimer = null;

  function getPromoHostEl() {
    return document.querySelector("[data-promo-announcement-host]");
  }

  function getAnnouncementBarEl() {
    const host = getPromoHostEl();
    return host?.closest(".announcement-bar") || document.querySelector(".announcement-bar");
  }

  function updateAnnouncementBarLayoutMode() {
    const bar = getAnnouncementBarEl();
    if (!bar) return;

    const host = getPromoHostEl();
    const hostVisible = Boolean(host && !host.hidden);
    const triggerVisible = Boolean(triggerEl && !triggerEl.hidden);

    bar.classList.remove("announcement-bar--promo-inline");
    bar.classList.toggle(
      "announcement-bar--promo-trigger",
      hostVisible && triggerVisible,
    );
  }

  function normalizeCode(code) {
    return (code || "").trim().toUpperCase();
  }

  function isCampaignDefaultCode(code) {
    return normalizeCode(code) === CAMPAIGN_DEFAULT_CODE;
  }

  function pickAttachedInfluencerCode(cart) {
    const influencer = getCartCodeEntries(cart).find(
      (entry) => !isCampaignDefaultCode(entry.code),
    );
    return influencer?.code || null;
  }

  function adoptAttachedInfluencerCode(cart, urlCode) {
    const influencer = pickAttachedInfluencerCode(cart);
    if (!influencer) return urlCode;
    if (urlCode && !isCampaignDefaultCode(urlCode)) return urlCode;
    if (normalizeCode(influencer) === normalizeCode(urlCode)) return urlCode;

    clearUrlActivationSession();
    clearProbeSessionForOtherCodes(influencer);
    clearPersistedPromoState();
    storeCode(influencer);
    return influencer;
  }

  function probeDoneKey(code) {
    return `${STORAGE_PROBE_DONE_PREFIX}${normalizeCode(code)}`;
  }

  function validationKey(code) {
    return `${STORAGE_VALIDATION_PREFIX}${normalizeCode(code)}`;
  }

  function decodeCodeSegment(segment) {
    if (!segment) return "";
    try {
      return decodeURIComponent(segment);
    } catch {
      return segment;
    }
  }

  function extractFromPath(pathname) {
    const match = pathname.match(/\/discount\/([^/?#]+)/i);
    if (!match) return null;
    return decodeCodeSegment(match[1]).trim() || null;
  }

  function extractFromQueryLegacy(search) {
    const raw = new URLSearchParams(search).get("discount");
    if (!raw) return null;

    let code = raw.trim();
    for (let i = 0; i < 2; i += 1) {
      try {
        const decoded = decodeURIComponent(code);
        if (decoded === code) break;
        code = decoded;
      } catch {
        break;
      }
    }
    return code.trim() || null;
  }

  const PREVIEW_QUERY_KEYS = ["preview_theme_id", "_ab", "_fd", "_sc"];

  function getPreviewQueryString() {
    const params = new URLSearchParams(window.location.search);
    const kept = new URLSearchParams();

    PREVIEW_QUERY_KEYS.forEach((key) => {
      if (params.has(key)) {
        kept.set(key, params.get(key));
      }
    });

    const serialized = kept.toString();
    return serialized ? `?${serialized}` : "";
  }

  function appendPreviewParams(path) {
    const previewQuery = getPreviewQueryString();
    if (!previewQuery) return path;

    const hashIndex = path.indexOf("#");
    const hash = hashIndex >= 0 ? path.slice(hashIndex) : "";
    const pathWithoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
    const joiner = pathWithoutHash.includes("?") ? "&" : "?";

    return `${pathWithoutHash}${joiner}${previewQuery.slice(1)}${hash}`;
  }

  function readStoredCode() {
    try {
      const code = sessionStorage.getItem(STORAGE_CODE);
      return code?.trim() || null;
    } catch {
      return null;
    }
  }

  function captureRedirectTarget() {
    const redirect = new URLSearchParams(window.location.search).get(
      "redirect",
    );
    if (!redirect) return null;

    try {
      const url = new URL(redirect, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      return url.pathname + url.search + url.hash;
    } catch {
      return null;
    }
  }

  function captureUrlCode() {
    const pathCode = extractFromPath(window.location.pathname);
    if (pathCode) return { code: pathCode, source: "path" };

    if (window.urlDiscountData?.detected && window.urlDiscountData.code) {
      return { code: window.urlDiscountData.code, source: "liquid" };
    }

    const legacyQueryCode = extractFromQueryLegacy(window.location.search);
    if (legacyQueryCode)
      return { code: legacyQueryCode, source: "query_legacy" };

    const referrerCode = extractCodeFromReferrer();
    if (referrerCode) return { code: referrerCode, source: "path" };

    // Do not treat discount_code cookie as a fresh activation source. A leftover
    // cookie with empty sessionStorage is a stale-session signal, not a new
    // /discount/CODE landing. Cart reconcile + gate cart confirmation handle attach.
    return null;
  }

  function isInitialActivation(captured) {
    if (!captured) return false;

    return (
      captured.source === "path" ||
      captured.source === "liquid" ||
      captured.source === "query_legacy" ||
      captured.source === "cart_reconcile"
    );
  }

  function isFreshUrlLanding() {
    return Boolean(window.urlDiscountData?.detected);
  }

  function shouldForceInlineFeedback() {
    try {
      return sessionStorage.getItem(STORAGE_FORCE_INLINE) === "1";
    } catch {
      return false;
    }
  }

  function setForceInlineFeedback(value) {
    try {
      if (value) {
        sessionStorage.setItem(STORAGE_FORCE_INLINE, "1");
      } else {
        sessionStorage.removeItem(STORAGE_FORCE_INLINE);
      }
    } catch {
      /* ignore */
    }
  }

  function markFreshUrlDiscountLanding(code) {
    if (!code) return;
    const normalized = normalizeCode(code);

    setForceInlineFeedback(true);
    clearInlineSeenForCode(code);

    if (!isDiscountReferrer() && !isFreshUrlLanding()) return;

    probeCompletedCodes.delete(normalized);
    try {
      sessionStorage.removeItem(probeDoneKey(code));
      sessionStorage.removeItem(validationKey(code));
    } catch {
      /* ignore */
    }
  }

  function isDiscountReferrer() {
    try {
      return /\/discount\//i.test(document.referrer || "");
    } catch {
      return false;
    }
  }

  function readUserOverride() {
    try {
      return sessionStorage.getItem(STORAGE_USER_OVERRIDE);
    } catch {
      return null;
    }
  }

  function setUserOverride(value) {
    try {
      if (value) {
        sessionStorage.setItem(STORAGE_USER_OVERRIDE, value);
      } else {
        sessionStorage.removeItem(STORAGE_USER_OVERRIDE);
      }
    } catch {
      /* ignore */
    }
  }

  function clearUserOverride() {
    setUserOverride(null);
  }

  function readLastCartCodes() {
    try {
      const raw = sessionStorage.getItem(STORAGE_LAST_CART_CODES);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  function writeLastCartCodes(cart) {
    const normalized = getCartCodeEntries(cart).map(
      (entry) => entry.normalized,
    );

    try {
      sessionStorage.setItem(
        STORAGE_LAST_CART_CODES,
        JSON.stringify(normalized),
      );
    } catch {
      /* ignore */
    }
  }

  function markUrlCodeSeenOnCart() {
    try {
      sessionStorage.setItem(STORAGE_URL_SEEN_ON_CART, "1");
    } catch {
      /* ignore */
    }
  }

  function hasUrlCodeSeenOnCart() {
    try {
      return sessionStorage.getItem(STORAGE_URL_SEEN_ON_CART) === "1";
    } catch {
      return false;
    }
  }

  function clearUrlActivationSession() {
    clearUserOverride();

    try {
      sessionStorage.removeItem(STORAGE_URL_SEEN_ON_CART);
      sessionStorage.removeItem(STORAGE_LAST_CART_CODES);
      sessionStorage.removeItem(STORAGE_MANUAL_AUTO_MINIMIZED);
      sessionStorage.removeItem(STORAGE_FORCE_INLINE);
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(STORAGE_INLINE_SEEN_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch {
      /* ignore */
    }
  }

  function isManualAutoMinimized() {
    try {
      return sessionStorage.getItem(STORAGE_MANUAL_AUTO_MINIMIZED) === "1";
    } catch {
      return false;
    }
  }

  function setManualAutoMinimized(value) {
    try {
      if (value) {
        sessionStorage.setItem(STORAGE_MANUAL_AUTO_MINIMIZED, "1");
      } else {
        sessionStorage.removeItem(STORAGE_MANUAL_AUTO_MINIMIZED);
      }
    } catch {
      /* ignore */
    }
  }

  function getCartCodeEntries(cart) {
    return (cart?.discount_codes || [])
      .filter((dc) => dc.code)
      .map((dc) => ({
        code: dc.code.trim(),
        normalized: normalizeCode(dc.code),
        applicable: dc.applicable,
      }));
  }

  function cartContainsCode(cart, code) {
    const normalized = normalizeCode(code);
    if (!normalized) return false;

    return getCartCodeEntries(cart).some(
      (entry) => entry.normalized === normalized,
    );
  }

  function getApplicableCodeEntries(cart) {
    return getCartCodeEntries(cart).filter((entry) => entry.applicable === true);
  }

  function detectUserOverrideFromCart(cart, urlCode) {
    const urlNorm = normalizeCode(urlCode);
    const cartNorms = getCartCodeEntries(cart).map((entry) => entry.normalized);
    const lastNorms = readLastCartCodes();
    const urlWasOnCart = Boolean(
      urlNorm && (hasUrlCodeSeenOnCart() || lastNorms.includes(urlNorm)),
    );

    if (!urlCode) {
      writeLastCartCodes(cart);
      return false;
    }

    if (urlWasOnCart && urlNorm && !cartNorms.includes(urlNorm)) {
      setUserOverride(cartNorms.length === 0 ? "removed" : "changed");
      writeLastCartCodes(cart);
      return true;
    }

    if (cartNorms.length === 0 && lastNorms.length > 0 && urlWasOnCart) {
      setUserOverride("removed");
      writeLastCartCodes(cart);
      return true;
    }

    if (
      cartNorms.length > 0 &&
      urlNorm &&
      !cartNorms.includes(urlNorm) &&
      lastNorms.length > 0
    ) {
      setUserOverride("changed");
      writeLastCartCodes(cart);
      return true;
    }

    if (urlNorm && cartNorms.includes(urlNorm)) {
      markUrlCodeSeenOnCart();
    }

    writeLastCartCodes(cart);
    return false;
  }

  function resolvePromoPresentation(cart, urlCode) {
    const override = readUserOverride();

    if (override === "removed") {
      return { show: false };
    }

    const entries = getCartCodeEntries(cart);
    const applicableEntries = entries.filter(
      (entry) => entry.applicable === true,
    );
    const itemCount = getRealCartLineCount(cart);
    const urlNorm = normalizeCode(urlCode);

    if (applicableEntries.length > 1) {
      const primary =
        applicableEntries.find(
          (entry) => urlNorm && entry.normalized === urlNorm,
        ) || applicableEntries[0];

      return {
        show: true,
        state: "multiple",
        displayCode: primary.code,
        count: applicableEntries.length,
      };
    }

    if (applicableEntries.length === 1) {
      const entry = applicableEntries[0];

      if (itemCount > 0) {
        if (entry.applicable === true) {
          return {
            show: true,
            state: "applied",
            displayCode: entry.code,
            count: 1,
          };
        }

        if (entry.applicable === false) {
          const entryNorm = normalizeCode(entry.code);
          if (urlNorm && entryNorm === urlNorm) {
            return {
              show: true,
              state: "stored",
              displayCode: entry.code,
              count: 1,
            };
          }

          return { show: false };
        }
      }

      if (entry.applicable === true) {
        return {
          show: true,
          state: itemCount > 0 ? "applied" : "stored",
          displayCode: entry.code,
          count: 1,
        };
      }
    }

    if (!urlCode) {
      const persisted = readPersistedPromoState();
      if (persisted?.code && !readUserOverride()) {
        return {
          show: true,
          state: persisted.state || "stored",
          displayCode: persisted.code,
          count: persisted.count || 1,
        };
      }

      return { show: false };
    }

    if (override === "changed" && entries.length === 0) {
      return { show: false };
    }

    if (itemCount > 0) {
      const validation = readValidationResult(urlCode);
      if (validation === "activated") {
        const entry = findCartDiscountEntry(cart, urlCode);
        if (entry?.applicable === true) {
          return { show: true, state: "applied", displayCode: urlCode, count: 1 };
        }

        return { show: true, state: "stored", displayCode: urlCode, count: 1 };
      }

      return { show: false };
    }

    const validation = readValidationResult(urlCode);
    if (validation === "activated") {
      return { show: true, state: "stored", displayCode: urlCode, count: 1 };
    }

    if (validation === "rejected") {
      clearPersistedPromoState();
      return { show: true, state: "rejected", displayCode: urlCode, count: 1 };
    }

    const attached = findCartDiscountEntry(cart, urlCode);
    if (attached?.applicable === false && !isCampaignDefaultCode(urlCode)) {
      return { show: false };
    }

    return { show: true, state: "pending", displayCode: urlCode, count: 1 };
  }

  function hasUrlPromoContext(displayCode) {
    const storedCode = readStoredCode();
    const normalizedDisplayCode = normalizeCode(displayCode);
    const normalizedStoredCode = normalizeCode(storedCode);

    if (
      normalizedStoredCode &&
      normalizedDisplayCode &&
      normalizedStoredCode === normalizedDisplayCode
    ) {
      return true;
    }

    if (displayCode && readValidationResult(displayCode)) {
      return true;
    }

    if (hasUrlCodeSeenOnCart()) {
      return true;
    }

    return false;
  }

  function hasExplicitUrlActivationInput() {
    if (window.urlDiscountData?.detected) return true;
    if (isDiscountReferrer()) return true;
    if (readStoredCode()) return true;
    if (hasUrlCodeSeenOnCart()) return true;

    try {
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(STORAGE_VALIDATION_PREFIX)) return true;
      }
    } catch {
      /* ignore */
    }

    return false;
  }

  function isAnnouncementState(state) {
    return state === "applied" || state === "multiple" || state === "stored";
  }

  function normalizePromoDisplayState(state, cart, displayCode) {
    if (
      state === "applied" ||
      state === "multiple" ||
      state === "stored" ||
      state === "rejected"
    ) {
      return state;
    }

    if (state === "activated") {
      const entry = cart ? findCartDiscountEntry(cart, displayCode) : null;
      if (entry?.applicable === true && getRealCartLineCount(cart) > 0) {
        return "applied";
      }

      return "stored";
    }

    return state;
  }

  function isInlineFeedbackState(state, urlContext) {
    if (!urlContext && !shouldForceInlineFeedback()) return false;
    if (state === "pending" || state === "checking") return true;
    if (shouldForceInlineFeedback() && state === "applied") return true;
    return state === "activated" || state === "rejected";
  }

  function inlineDisplayState(state) {
    if (state === "pending") return "checking";
    if (shouldForceInlineFeedback() && state === "applied") return "activated";
    return state;
  }

  function inlineSeenKey(state, code) {
    return `${STORAGE_INLINE_SEEN_PREFIX}${normalizeCode(code)}:${state}`;
  }

  function clearInlineSeenForCode(code) {
    const normalized = normalizeCode(code);
    if (!normalized) return;

    try {
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(`${STORAGE_INLINE_SEEN_PREFIX}${normalized}:`)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch {
      /* ignore */
    }
  }

  function shouldShowInlineFeedback(state, code) {
    if (state === "checking" || state === "pending") return true;
    if (shouldForceInlineFeedback()) return true;
    if (isFreshUrlLanding()) return true;
    try {
      return sessionStorage.getItem(inlineSeenKey(state, code)) !== "1";
    } catch {
      return true;
    }
  }

  function markInlineSeen(state, code) {
    if (state === "checking") return;
    try {
      sessionStorage.setItem(inlineSeenKey(state, code), "1");
    } catch {
      /* ignore */
    }
  }

  function clearProbeSessionForOtherCodes(code) {
    const normalized = normalizeCode(code);
    if (!normalized) return;

    try {
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (!key) continue;

        if (
          (key.startsWith(STORAGE_PROBE_DONE_PREFIX) ||
            key.startsWith(STORAGE_VALIDATION_PREFIX)) &&
          !key.endsWith(normalized)
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch {
      /* ignore */
    }
  }

  function storeCode(code) {
    const trimmed = code?.trim();
    if (!trimmed) return false;

    const previous = readStoredCode();
    if (previous && normalizeCode(previous) !== normalizeCode(trimmed)) {
      clearProbeSessionForOtherCodes(trimmed);
      clearInlineSeenForCode(trimmed);
    }

    try {
      sessionStorage.setItem(STORAGE_CODE, trimmed);
      return true;
    } catch {
      return false;
    }
  }

  function storeRedirect(path) {
    if (!path) return;
    try {
      sessionStorage.setItem(STORAGE_REDIRECT, path);
    } catch {
      /* ignore */
    }
  }

  function readRedirect() {
    try {
      return sessionStorage.getItem(STORAGE_REDIRECT);
    } catch {
      return null;
    }
  }

  function isMinimized() {
    try {
      if (sessionStorage.getItem(STORAGE_MINIMIZED) === "1") return true;

    } catch {
      return false;
    }

    return false;
  }

  function setMinimized(minimized) {
    try {
      if (minimized) {
        sessionStorage.setItem(STORAGE_MINIMIZED, "1");
      } else {
        sessionStorage.removeItem(STORAGE_MINIMIZED);
      }
    } catch {
      /* ignore */
    }
  }

  function clearMinimizedOnFreshCapture(source) {
    if (
      source === "path" ||
      source === "liquid" ||
      source === "cart_reconcile"
    ) {
      setMinimized(false);
    }
  }

  function readValidationResult(code) {
    try {
      return sessionStorage.getItem(validationKey(code));
    } catch {
      return null;
    }
  }

  function writeValidationResult(code, result) {
    if (!code || !result) return;
    try {
      sessionStorage.setItem(validationKey(code), result);
    } catch {
      /* ignore */
    }
  }

  function persistPromoState(code, state, count = 1) {
    if (!code || !state) return;
    if (state === "rejected" || state === "pending" || state === "checking") {
      return;
    }

    const normalizedState =
      state === "activated" ? "stored" : state;

    if (!["stored", "applied", "multiple"].includes(normalizedState)) {
      return;
    }

    try {
      localStorage.setItem(
        STORAGE_PERSISTED_PROMO,
        JSON.stringify({
          code: code.trim(),
          state: normalizedState,
          count: Number(count) > 0 ? Number(count) : 1,
          timestamp: Date.now(),
        }),
      );
    } catch {
      /* ignore */
    }
  }

  function readPersistedPromoState() {
    try {
      const raw = localStorage.getItem(STORAGE_PERSISTED_PROMO);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed?.code || !parsed?.timestamp) return null;

      if (Date.now() - Number(parsed.timestamp) > PERSISTED_PROMO_TTL_MS) {
        localStorage.removeItem(STORAGE_PERSISTED_PROMO);
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  }

  function clearPersistedPromoState() {
    try {
      localStorage.removeItem(STORAGE_PERSISTED_PROMO);
    } catch {
      /* ignore */
    }
  }

  function restoreValidationFromPersistence(code) {
    if (!code || readUserOverride()) return;

    const persisted = readPersistedPromoState();
    if (
      !persisted?.code ||
      normalizeCode(persisted.code) !== normalizeCode(code)
    ) {
      return;
    }

    if (!readValidationResult(code)) {
      writeValidationResult(code, "activated");
    }
  }

  function hasConfirmedInfluencerOwnership(code) {
    if (!code || isCampaignDefaultCode(code) || readUserOverride()) return false;
    if (readValidationResult(code) === "activated") return true;

    const persisted = readPersistedPromoState();
    return Boolean(
      persisted?.code &&
        normalizeCode(persisted.code) === normalizeCode(code) &&
        (persisted.state === "stored" || persisted.state === "applied"),
    );
  }

  function hasActiveInfluencerSession(code = readStoredCode(), cart = null) {
    if (!code || isCampaignDefaultCode(code) || readUserOverride()) return false;
    if (readValidationResult(code) === "activated") return true;
    return Boolean(findCartDiscountEntry(cart, code)?.applicable === true);
  }

  function getDiscountPathCode(url) {
    try {
      const href = String(url || "");
      if (!href) return "";
      const resolved = href.startsWith("http")
        ? new URL(href)
        : new URL(href, window.location.origin);
      const match = resolved.pathname.match(/\/discount\/([^/]+)/i);
      return match ? normalizeCode(decodeCodeSegment(match[1])) : "";
    } catch {
      return "";
    }
  }

  function shouldBlockPassiveGlouraDiscountFetch(url) {
    return (
      getDiscountPathCode(url) === CAMPAIGN_DEFAULT_CODE &&
      hasActiveInfluencerSession()
    );
  }

  function installDiscountFetchGuard() {
    if (window.__glouraDiscountFetchGuard) return;
    window.__glouraDiscountFetchGuard = true;

    const originalFetch = window.fetch;
    if (typeof originalFetch !== "function") return;

    window.fetch = function (...args) {
      const raw =
        args[0] && typeof args[0] === "object" && args[0].url
          ? args[0].url
          : args[0];
      if (shouldBlockPassiveGlouraDiscountFetch(raw)) {
        return Promise.resolve(new Response("", { status: 204 }));
      }
      return originalFetch.apply(this, args);
    };
  }

  function cartHasCampaignDefaultCode(cart) {
    return getCartCodeEntries(cart).some((entry) =>
      isCampaignDefaultCode(entry.code),
    );
  }

  function cartHasNonDefaultCode(cart) {
    return getCartCodeEntries(cart).some(
      (entry) => !isCampaignDefaultCode(entry.code),
    );
  }

  function customerDismissedCampaignDefault() {
    if (readUserOverride() !== "removed") return false;
    const stored = readStoredCode();
    return !stored || isCampaignDefaultCode(stored);
  }

  function rememberExplicitCampaignDefaultRemoval(cart, event) {
    if (readUserOverride()) return;
    if (!isExplicitCartDiscountAction(event)) return;
    if (!cart || getRealCartLineCount(cart) === 0) return;
    if (cartHasCampaignDefaultCode(cart) || cartHasNonDefaultCode(cart)) return;
    if (!readLastCartCodes().some((code) => isCampaignDefaultCode(code))) return;
    setUserOverride("removed");
  }

  function campaignDefaultEnsureKey(cart) {
    const items = (cart?.items || [])
      .filter((item) => !isProbeLineItem(item))
      .map((item) => `${item.variant_id}:${item.quantity}`)
      .join(",");
    return `${cart?.token || ""}:${items}`;
  }

  function shouldEnsureCampaignDefaultOnEligibleCart(cart, event) {
    if (!cart || getRealCartLineCount(cart) === 0) return false;
    if (campaignDefaultEnsureInFlight || syncInFlight) return false;
    if (lastCampaignDefaultEnsureKey === campaignDefaultEnsureKey(cart)) {
      return false;
    }
    if (customerDismissedCampaignDefault()) return false;
    if (hasActiveInfluencerSession(readStoredCode(), cart)) return false;

    const stored = readStoredCode();
    if (
      stored &&
      !isCampaignDefaultCode(stored) &&
      readUserOverride() !== "removed"
    ) {
      return false;
    }

    if (cartHasNonDefaultCode(cart)) {
      lastCampaignDefaultEnsureKey = "";
      return false;
    }
    if (cartHasCampaignDefaultCode(cart)) {
      lastCampaignDefaultEnsureKey = "";
      return false;
    }

    if (
      isExplicitCartDiscountAction(event) &&
      readLastCartCodes().some((code) => isCampaignDefaultCode(code))
    ) {
      return false;
    }

    return true;
  }

  async function ensureCampaignDefaultOnEligibleCart(cart, event) {
    rememberExplicitCampaignDefaultRemoval(cart, event);
    if (!shouldEnsureCampaignDefaultOnEligibleCart(cart, event)) {
      return cart;
    }

    campaignDefaultEnsureInFlight = true;
    lastCampaignDefaultEnsureKey = campaignDefaultEnsureKey(cart);
    try {
      const updated = await applyUrlCodeToCart(CAMPAIGN_DEFAULT_CODE, cart, {
        exclusive: true,
        campaignDefault: true,
        codes: [CAMPAIGN_DEFAULT_CODE],
      });
      if (updated) {
        writeLastCartCodes(updated);
        lastCampaignDefaultEnsureKey = cartHasCampaignDefaultCode(updated)
          ? ""
          : campaignDefaultEnsureKey(updated);
        return updated;
      }
      return cart;
    } finally {
      campaignDefaultEnsureInFlight = false;
    }
  }

  function listNonDefaultCartCodes(cart, preferred) {
    const codes = [];
    const seen = new Set();
    const add = (code) => {
      const normalized = normalizeCode(code);
      if (!normalized || isCampaignDefaultCode(normalized) || seen.has(normalized)) {
        return;
      }
      seen.add(normalized);
      codes.push(String(code).trim());
    };

    add(preferred);
    getCartCodeEntries(cart).forEach((entry) => {
      if (entry.applicable === true) add(entry.code);
    });
    return codes;
  }

  function pickCheckoutOwnedCode(cart) {
    if (readUserOverride()) return "";
    const stored = readStoredCode();
    if (hasActiveInfluencerSession(stored, cart)) return stored;
    const attached = getApplicableCodeEntries(cart).find(
      (entry) => !isCampaignDefaultCode(entry.code),
    );
    return attached?.code || "";
  }

  async function reassertOwnedCodeIfGlouraReappears() {
    await sleep(GLOURA_REASSERT_DELAY_MS);
    if (readUserOverride()) return;
    const urlCode = readStoredCode();
    if (!hasConfirmedInfluencerOwnership(urlCode)) return;
    const cart = await fetchCart().catch(() => null);
    if (!cart || !cartHasCampaignDefaultCode(cart)) return;
    await enforceStoredCodeAfterCartQualifies(cart, urlCode);
  }

  function clearExplicitCartDiscountAction() {
    explicitDiscountActionPending = false;
    if (explicitDiscountActionTimer) {
      clearTimeout(explicitDiscountActionTimer);
      explicitDiscountActionTimer = null;
    }
  }

  function markExplicitCartDiscountAction() {
    explicitDiscountActionPending = true;
    if (explicitDiscountActionTimer) {
      clearTimeout(explicitDiscountActionTimer);
    }
    explicitDiscountActionTimer = setTimeout(
      clearExplicitCartDiscountAction,
      EXPLICIT_DISCOUNT_ACTION_MS,
    );
  }

  function isExplicitCartDiscountAction(event) {
    const source =
      event?.source || event?.detail?.source || event?.extra?.source || "";
    if (source === "cart-discount") return true;
    return explicitDiscountActionPending;
  }

  function readSubmittedCartFormCode(event) {
    const source =
      event?.source || event?.detail?.source || event?.extra?.source || "";
    if (source !== "cart-discount") return "";

    const submitted =
      event?.submittedCode ||
      event?.detail?.submittedCode ||
      event?.extra?.submittedCode ||
      "";
    const normalized = normalizeCode(submitted);
    if (!normalized || isCampaignDefaultCode(normalized)) return "";
    return submitted.trim();
  }

  function claimVerifiedCartFormCode(cart, event) {
    const submitted = readSubmittedCartFormCode(event);
    if (!submitted) return null;
    if (findCartDiscountEntry(cart, submitted)?.applicable !== true) return null;
    if (cartHasCampaignDefaultCode(cart)) return null;

    const previous = readStoredCode();
    if (!previous || normalizeCode(previous) !== normalizeCode(submitted)) {
      clearUrlActivationSession();
      clearProbeSessionForOtherCodes(submitted);
      clearPersistedPromoState();
    } else {
      clearUserOverride();
    }

    storeCode(submitted);
    writeValidationResult(submitted, "activated");
    persistPromoState(submitted, "stored");
    markUrlCodeSeenOnCart();
    markProbeDone(submitted);
    writeLastCartCodes(cart);
    return submitted;
  }

  function consumeExplicitCartDiscountAction(event) {
    const explicit = isExplicitCartDiscountAction(event);
    if (explicit) clearExplicitCartDiscountAction();
    return explicit;
  }

  function bindExplicitCartDiscountTracking() {
    document.addEventListener(
      "click",
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.closest("[is='cart-discount-remove']")) {
          markExplicitCartDiscountAction();
        }
      },
      true,
    );
    document.addEventListener(
      "submit",
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.closest("[is='cart-discount']")) {
          markExplicitCartDiscountAction();
        }
      },
      true,
    );
  }

  function rehydratePromoFromPersistence() {
    if (readUserOverride()) return null;

    const persisted = readPersistedPromoState();
    if (!persisted?.code) return null;

    storeCode(persisted.code);
    writeValidationResult(persisted.code, "activated");
    return persisted.code;
  }

  function isProbeDone(code) {
    const normalized = normalizeCode(code);
    if (!normalized) return false;

    if (probeCompletedCodes.has(normalized)) return true;

    try {
      return sessionStorage.getItem(probeDoneKey(code)) === "1";
    } catch {
      return probeCompletedCodes.has(normalized);
    }
  }

  function markProbeDone(code) {
    const normalized = normalizeCode(code);
    if (!normalized) return;

    probeCompletedCodes.add(normalized);

    try {
      sessionStorage.setItem(probeDoneKey(code), "1");
    } catch {
      /* ignore */
    }
  }

  function hasResolvedValidation(code) {
    const validation = readValidationResult(code);
    return validation === "activated" || validation === "rejected";
  }

  function getRealCartLineCount(cart) {
    return (cart?.items || [])
      .filter((item) => !isProbeLineItem(item))
      .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  }

  function isProbeNetworkError(error) {
    if (!error) return false;
    if (error.probeNetwork === true) return true;
    if (error.name === "TypeError") return true;
    return false;
  }

  function createProbeNetworkError(message) {
    const error = new Error(message);
    error.probeNetwork = true;
    return error;
  }

  function cartConfirmsCodeActivation(cart, urlCode) {
    if (!urlCode) return false;

    const entry = findCartDiscountEntry(cart, urlCode);
    if (!entry) return false;
    return entry.applicable === true || isCampaignDefaultCode(urlCode);
  }

  function shouldPresentFreshActivationToast(urlCode) {
    if (!shouldForceInlineFeedback() && !isFreshUrlLanding()) return false;
    return shouldShowInlineFeedback("activated", urlCode);
  }

  function presentUrlValidationResult(urlCode, state, cart) {
    writeValidationResult(urlCode, state);
    markProbeDone(urlCode);
    markUrlCodeSeenOnCart();
    if (state === "activated") {
      persistPromoState(urlCode, "stored");
    } else if (state === "rejected") {
      clearPersistedPromoState();
    }
    markFreshUrlDiscountLanding(urlCode);
    applyPromoResult(urlCode, state, isMinimized(), 1, cart);
    publishState(urlCode, state, cart, { count: 1 });
  }

  function tryPresentCartActivationOnly(cart, urlCode) {
    if (!cartConfirmsCodeActivation(cart, urlCode)) return false;
    if (!shouldPresentFreshActivationToast(urlCode)) return false;

    presentUrlValidationResult(urlCode, "activated", cart);
    return true;
  }

  async function shouldRunProbe(cart, displayCode) {
    if (!hasExplicitUrlActivationInput()) return false;
    if (probeInFlight) return false;
    if (readUserOverride()) return false;
    if (getRealCartLineCount(cart) > 0) return false;
    if (!displayCode) return false;
    const attached = findCartDiscountEntry(cart, displayCode);
    if (attached?.applicable === false && !isCampaignDefaultCode(displayCode)) {
      return false;
    }
    if (isProbeDone(displayCode)) return false;
    if (hasResolvedValidation(displayCode)) return false;

    const variantId = await resolveProbeVariantId();
    if (!variantId) {
      if (!readValidationResult(displayCode)) {
        writeValidationResult(displayCode, "pending");
      }
      return false;
    }

    return true;
  }

  function formatMessage(template, data) {
    if (!template) return "";

    const code = typeof data === "string" ? data : data?.code || "";
    const count = typeof data === "object" ? data?.count : "";
    const savings = typeof data === "object" ? data?.savings || "" : "";

    return template
      .replace(/\{\{\s*code\s*\}\}/gi, code)
      .replace(/\{\{\s*count\s*\}\}/gi, String(count ?? ""))
      .replace(/\{\{\s*savings\s*\}\}/gi, savings);
  }

  function formatSavingsMoney(cents, currency) {
    if (!Number.isFinite(cents) || cents <= 0) return "";

    if (
      window.FoxTheme?.Currency?.formatMoney &&
      window.FoxTheme?.settings?.moneyFormat
    ) {
      return FoxTheme.Currency.formatMoney(
        cents,
        FoxTheme.settings.moneyFormat,
      );
    }

    try {
      const locale =
        document.documentElement.lang ||
        window.Shopify?.locale ||
        "de-DE";

      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency || "EUR",
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${currency || "EUR"}`;
    }
  }

  function readCartSavingsPayload() {
    const nodes = document.querySelectorAll("[data-cart-savings-payload]");
    for (let i = nodes.length - 1; i >= 0; i -= 1) {
      const text = nodes[i].textContent?.trim();
      if (!text) continue;

      try {
        return JSON.parse(text);
      } catch {
        continue;
      }
    }

    return null;
  }

  function syncPromoCartSavingsPayload() {
    const canonical = document.querySelector(
      "[data-promo-cart-savings-canonical]",
    );
    if (!canonical) return;

    const nodes = document.querySelectorAll("[data-cart-savings-payload]");
    for (let i = nodes.length - 1; i >= 0; i -= 1) {
      const node = nodes[i];
      if (node === canonical) continue;

      const text = node.textContent?.trim();
      if (!text) continue;

      try {
        JSON.parse(text);
        canonical.textContent = text;
        return;
      } catch {
        continue;
      }
    }
  }

  function schedulePromoCartSavingsPayloadSync() {
    window.requestAnimationFrame(() => {
      syncPromoCartSavingsPayload();
      window.setTimeout(syncPromoCartSavingsPayload, 150);
    });
  }

  function getCodeRowsFromPayload(payload) {
    return (payload?.rows || []).filter((row) => row.is_code);
  }

  function getMatchingCodeRows(payload, displayCode) {
    const normalized = normalizeCode(displayCode);
    return getCodeRowsFromPayload(payload).filter(
      (row) => normalizeCode(row.code) === normalized,
    );
  }

  function hasAutomaticBenefitsInPayload(payload) {
    return (payload?.rows || []).some((row) => !row.is_code);
  }

  function cartCurrencyFromPayload(payload) {
    return (
      payload?.currency ||
      window.Shopify?.currency?.active ||
      window.Shopify?.currency?.iso_code ||
      "EUR"
    );
  }

  function createPromoBreakdownRow(row) {
    const item = document.createElement("div");
    item.className = "promo-activation-popover__row";

    const copy = document.createElement("div");
    copy.className = "promo-activation-popover__row-copy";

    const icon = document.createElement("span");
    icon.className = "promo-activation-popover__row-icon";
    icon.setAttribute("aria-hidden", "true");
    icon.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 14 15" fill="none"><path d="M2.31547 8.06547C2.23358 7.98349 2.18755 7.87236 2.1875 7.75648V2.6875H7.25648C7.37236 2.68755 7.48349 2.73358 7.56547 2.81547L12.997 8.24703C13.079 8.32907 13.1251 8.44031 13.1251 8.55629C13.1251 8.67227 13.079 8.78351 12.997 8.86555L8.36719 13.497C8.28515 13.579 8.17391 13.6251 8.05793 13.6251C7.94195 13.6251 7.83071 13.579 7.74867 13.497L2.31547 8.06547Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M4.59375 5.75C4.95619 5.75 5.25 5.45619 5.25 5.09375C5.25 4.73131 4.95619 4.4375 4.59375 4.4375C4.23131 4.4375 3.9375 4.73131 3.9375 5.09375C3.9375 5.45619 4.23131 5.75 4.59375 5.75Z" fill="currentColor"/></svg>';

    const title = document.createElement("p");
    title.className = "promo-activation-popover__row-title";
    title.textContent = row.title || "";

    copy.appendChild(icon);
    copy.appendChild(title);

    const amount = document.createElement("div");
    amount.className = "promo-activation-popover__row-amount";
    amount.textContent = row.amount_money || "";

    item.appendChild(copy);
    item.appendChild(amount);

    return item;
  }

  function renderPromoBreakdown(payload, displayCode, state) {
    if (!breakdownEl || !totalEl || !totalValueEl) return false;

    breakdownEl.innerHTML = "";
    breakdownEl.hidden = true;
    totalEl.hidden = true;
    if (totalLabelEl) totalLabelEl.textContent = "";
    if (totalValueEl) totalValueEl.textContent = "";

    if (
      !payload ||
      state === "stored" ||
      state === "activated" ||
      state === "rejected"
    ) {
      return false;
    }

    const rows =
      state === "applied"
        ? getMatchingCodeRows(payload, displayCode)
        : state === "multiple"
          ? getCodeRowsFromPayload(payload)
          : [];

    if (!rows.length) return false;

    breakdownEl.hidden = false;
    rows.forEach((row) => {
      breakdownEl.appendChild(createPromoBreakdownRow(row));
    });

    const codeTotal = rows.reduce(
      (sum, row) => sum + Number(row.amount || 0),
      0,
    );

    if (rows.length > 1 && codeTotal > 0) {
      totalEl.hidden = false;
      if (totalLabelEl) {
        totalLabelEl.textContent =
          strings.details_codes_total_label || "Rabattcodes gesamt";
      }
      totalValueEl.textContent = `-${formatSavingsMoney(
        codeTotal,
        cartCurrencyFromPayload(payload),
      )}`;
    }

    return true;
  }

  function buildPromoDetails(cart, displayCode, state, count = 1) {
    const displayState = normalizePromoDisplayState(state, cart, displayCode);
    const payload = readCartSavingsPayload();
    const codeRows = getCodeRowsFromPayload(payload);
    const codeCount = codeRows.length || count;
    const automaticHint = hasAutomaticBenefitsInPayload(payload)
      ? strings.details_automatic_benefits_hint ||
        "Weitere automatische Vorteile im Warenkorb."
      : "";

    if (displayState === "multiple") {
      const matchingRows = getCodeRowsFromPayload(payload);
      return {
        triggerAria: formatMessage(strings.trigger_aria_multiple_codes, {
          count: codeCount,
        }),
        title: formatMessage(strings.details_multiple_codes_title, {
          count: codeCount,
        }),
        body: matchingRows.length
          ? ""
          : strings.details_multiple_codes_generic ||
            "Aktive Rabattcodes werden im Warenkorb angezeigt.",
        meta: automaticHint,
      };
    }

    if (displayState === "applied") {
      return {
        triggerAria: strings.trigger_aria_active || "Rabatt aktiv",
        title: strings.details_code_active_title || "Rabattcode ist aktiv",
        body: "",
        meta: automaticHint,
      };
    }

    if (displayState === "stored") {
      return {
        triggerAria:
          strings.trigger_aria_saved_generic || "Rabattcode gespeichert",
        title:
          strings.details_code_saved_title_generic || "Rabattcode gespeichert",
        body:
          strings.details_code_saved_body ||
          "Wird angewendet, sobald Artikel im Warenkorb sind.",
        meta: "",
      };
    }

    return {
      triggerAria: strings.trigger_aria_active || "Rabatt aktiv",
      title: strings.popover_active || "Rabatt aktiv.",
      body: strings.details_generic || "",
      meta: "",
    };
  }

  function populatePromoPopover(promoDetails, options = {}) {
    const deferMeta = options.deferMeta === true;

    if (messageEl) {
      messageEl.textContent = promoDetails?.title || "";
    }

    if (messageBodyEl) {
      if (promoDetails?.body) {
        messageBodyEl.textContent = promoDetails.body;
        messageBodyEl.hidden = false;
      } else {
        messageBodyEl.textContent = "";
        messageBodyEl.hidden = true;
      }
    }

    if (messageMetaEl) {
      if (!deferMeta && promoDetails?.meta) {
        messageMetaEl.textContent = promoDetails.meta;
        messageMetaEl.hidden = false;
      } else {
        messageMetaEl.textContent = "";
        messageMetaEl.hidden = true;
      }
    }

    if (triggerLabelEl) {
      triggerLabelEl.textContent = promoDetails?.triggerAria || "";
    }
  }

  function renderPromoMeta(promoDetails) {
    if (!messageMetaEl) return;

    if (promoDetails?.meta) {
      messageMetaEl.textContent = `(${promoDetails.meta})`;
      messageMetaEl.hidden = false;
    } else {
      messageMetaEl.textContent = "";
      messageMetaEl.hidden = true;
    }
  }

  function populatePromoPopoverWithBreakdown(
    promoDetails,
    displayCode,
    state,
  ) {
    populatePromoPopover(promoDetails, { deferMeta: true });
    renderPromoBreakdown(readCartSavingsPayload(), displayCode, state);
    renderPromoMeta(promoDetails);
  }

  function clearInlineSuccessTimer() {
    if (inlineSuccessTimer) {
      window.clearTimeout(inlineSuccessTimer);
      inlineSuccessTimer = null;
    }
  }

  function mountAnnouncementElements() {
    const currentHost = getPromoHostEl();
    if (!currentHost) return false;

    if (triggerEl && !currentHost.contains(triggerEl)) {
      currentHost.appendChild(triggerEl);
    }

    return true;
  }

  function scheduleAnnouncementRemount() {
    if (announcementRemountTimer) {
      window.clearTimeout(announcementRemountTimer);
    }

    announcementRemountTimer = window.setTimeout(() => {
      announcementRemountTimer = null;
      const currentHost = getPromoHostEl();
      if (!currentHost) return;

      const needsMount = triggerEl && !currentHost.contains(triggerEl);

      if (!needsMount) return;

      mountAnnouncementElements();
      const urlCode = readStoredCode();
      if (!urlCode) return;

      refreshUI().catch(() => {});
    }, 150);
  }

  function bindAnnouncementObserver() {
    if (announcementObserver) return;

    announcementObserver = new MutationObserver(() => {
      scheduleAnnouncementRemount();
    });

    const headerGroup = document.querySelector(
      ".shopify-section-group-header-group",
    );

    if (headerGroup) {
      announcementObserver.observe(headerGroup, {
        childList: true,
        subtree: true,
      });
      return;
    }

    announcementObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function hideInlineFeedback() {
    clearInlineSuccessTimer();
    if (!inlineEl) return;

    inlineEl.classList.remove(
      "is-visible",
      "promo-activation-inline--success",
      "promo-activation-inline--rejected",
      "promo-activation-inline--checking",
      "promo-activation-inline--pending",
    );

    window.setTimeout(() => {
      if (inlineEl.classList.contains("is-visible")) return;
      inlineEl.hidden = true;
    }, 220);

    currentInlineState = null;
    currentInlineCode = null;
  }

  function finishInlineFeedback() {
    clearInlineSuccessTimer();
    const state = currentInlineState;
    const code = currentInlineCode;

    if (state && code) {
      markInlineSeen(state, code);
    }

    setForceInlineFeedback(false);
    hideInlineFeedback();
    closePopover();

    if (state === "activated" || state === "applied") {
      refreshUI().catch(() => {});
      return;
    }

    if (state === "rejected" || state === "checking" || state === "pending") {
      if (triggerEl) triggerEl.hidden = true;
      const currentHost = getPromoHostEl();
      if (currentHost) currentHost.hidden = true;
      updateAnnouncementBarLayoutMode();
      return;
    }

    refreshUI().catch(() => {});
  }

  function showInlineFeedback(state, displayCode) {
    if (!inlineEl || !inlineMessageEl) return;
    const displayState = inlineDisplayState(state);

    clearInlineSuccessTimer();
    currentInlineState = displayState;
    currentInlineCode = displayCode;
    inlineMessageEl.textContent = formatMessage(
      strings[displayState] || strings.checking,
      displayCode,
    );

    inlineEl.classList.remove(
      "promo-activation-inline--success",
      "promo-activation-inline--rejected",
      "promo-activation-inline--checking",
      "promo-activation-inline--pending",
    );

    if (displayState === "activated") {
      inlineEl.classList.add("promo-activation-inline--success");
    } else if (displayState === "rejected") {
      inlineEl.classList.add("promo-activation-inline--rejected");
    } else if (displayState === "checking") {
      inlineEl.classList.add("promo-activation-inline--checking");
    } else if (displayState === "pending") {
      inlineEl.classList.add("promo-activation-inline--pending");
    }

    inlineEl.hidden = false;
    requestAnimationFrame(() => {
      inlineEl.classList.add("is-visible");
    });

    if (triggerEl) triggerEl.hidden = true;
    updateAnnouncementBarLayoutMode();

    if (displayState === "activated") {
      inlineSuccessTimer = window.setTimeout(() => {
        inlineSuccessTimer = null;
        finishInlineFeedback();
      }, INLINE_SUCCESS_DISMISS_MS);
    }
  }

  function positionPopover() {
    if (!popoverEl || popoverEl.hidden || !triggerEl) return;

    const triggerRect = triggerEl.getBoundingClientRect();
    const popoverRect = popoverEl.getBoundingClientRect();
    const viewportPadding = 10;
    const preferredLeft =
      triggerRect.left - popoverRect.width + triggerRect.width;
    const left = Math.min(
      window.innerWidth - popoverRect.width - viewportPadding,
      Math.max(viewportPadding, preferredLeft),
    );
    const top = Math.min(
      window.innerHeight - popoverRect.height - viewportPadding,
      triggerRect.bottom + 10,
    );

    popoverEl.style.left = `${left}px`;
    popoverEl.style.top = `${top}px`;
  }

  function openPopover() {
    if (!popoverEl) return;
    popoverEl.hidden = false;
    requestAnimationFrame(() => {
      popoverEl.classList.add("is-open");
      requestAnimationFrame(() => {
        positionPopover();
      });
    });
    if (triggerEl) triggerEl.setAttribute("aria-expanded", "true");
  }

  function closePopover() {
    if (!popoverEl) return;
    popoverEl.classList.remove("is-open");
    popoverEl.hidden = true;
    if (triggerEl) triggerEl.setAttribute("aria-expanded", "false");
  }

  function findCartDiscountEntry(cart, displayCode) {
    const normalized = normalizeCode(displayCode);
    if (!normalized) return null;

    return (
      (cart?.discount_codes || []).find(
        (dc) => dc.code && normalizeCode(dc.code) === normalized,
      ) || null
    );
  }

  /* A probe line is recognised by its marker property only. Matching by
     variant id as well deleted the shopper's REAL line of the same variant
     after the probe ran (the remembered probe variant stayed in
     sessionStorage), so the first add-to-cart after a /discount/ link
     vanished and only the second one stuck. */
  function isProbeLineItem(item) {
    const props = item?.properties || {};
    const marker = props[PROBE_LINE_PROPERTY];
    return marker === "1" || marker === 1 || marker === true;
  }

  function getProbeVariantIds() {
    const ids = new Set();
    const configured = getConfiguredVerificationVariantId();
    if (configured) ids.add(String(configured));

    try {
      const stored = sessionStorage.getItem(STORAGE_PROBE_VARIANT);
      if (stored) ids.add(String(stored));
    } catch {
      /* ignore */
    }

    return ids;
  }

  function rememberProbeVariantId(variantId) {
    if (!variantId) return;
    try {
      sessionStorage.setItem(STORAGE_PROBE_VARIANT, String(variantId));
    } catch {
      /* ignore */
    }
  }

  function clearProbeVariantId() {
    try {
      sessionStorage.removeItem(STORAGE_PROBE_VARIANT);
    } catch {
      /* ignore */
    }
  }

  function cartHasProbeLines(cart) {
    return Boolean((cart?.items || []).some((item) => isProbeLineItem(item)));
  }

  async function scrubProbeLines(cart) {
    if (probeInFlight || probeScrubInFlight) return cart;
    if (!cartHasProbeLines(cart)) return cart;

    probeScrubInFlight = true;
    try {
      await removeProbeLinesByKeys([], cart);

      let cleanCart = await fetchCart().catch(() => null);
      if (cleanCart && cartHasProbeLines(cleanCart)) {
        await removeProbeLinesByKeys([], cleanCart);
        cleanCart = await fetchCart().catch(() => null);
      }

      if (cleanCart && !cartHasProbeLines(cleanCart)) {
        clearProbeVariantId();
        publishCartUpdate(cleanCart, null);
      }

      return cleanCart || cart;
    } finally {
      probeScrubInFlight = false;
    }
  }

  async function ensureCartWithoutProbeLines(cart) {
    if (!cartHasProbeLines(cart)) return cart;
    return (await scrubProbeLines(cart)) || cart;
  }

  function getConfiguredVerificationVariantId() {
    const fromStrip = root.dataset.verificationVariantId?.trim();
    if (fromStrip) return fromStrip;

    const fromSettings = window.FoxTheme?.settings?.promoVerificationVariantId;
    return fromSettings?.trim() || null;
  }

  function getCurrentPageVariantId() {
    const contextVariant = window.glouraPromoProbeContext?.variantId;
    if (contextVariant) return String(contextVariant);

    const variantParam = new URLSearchParams(window.location.search).get(
      "variant",
    );
    return variantParam?.trim() || null;
  }

  function extractProductHandle(pathname) {
    const match = pathname.match(/\/products\/([^/?#]+)/i);
    return match ? decodeCodeSegment(match[1]) : null;
  }

  async function getVariantFromProductPath(pathname) {
    const handle = extractProductHandle(pathname);
    if (!handle) return null;

    const rootPath = window.Shopify?.routes?.root || "/";
    const productUrl = `${rootPath}products/${encodeURIComponent(handle)}.js`;

    try {
      const response = await fetch(productUrl, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return null;

      const product = await response.json();
      const variants = product?.variants || [];
      const available = variants.find((variant) => variant.available);
      const chosen = available || variants[0];
      return chosen?.id ? String(chosen.id) : null;
    } catch {
      return null;
    }
  }

  async function resolveProbeVariantId() {
    const currentPageVariant = getCurrentPageVariantId();
    if (currentPageVariant) return currentPageVariant;

    const redirectPath = readRedirect();
    if (redirectPath) {
      try {
        const redirectUrl = new URL(redirectPath, window.location.origin);
        const redirectVariant = new URLSearchParams(redirectUrl.search).get(
          "variant",
        );
        if (redirectVariant) return redirectVariant.trim();

        const redirectProductVariant = await getVariantFromProductPath(
          redirectUrl.pathname,
        );
        if (redirectProductVariant) return redirectProductVariant;
      } catch {
        /* ignore */
      }
    }

    return getConfiguredVerificationVariantId();
  }

  /**
   * Shopify CartDiscountCode.applicable — whether the code applies to current cart contents.
   * @see https://shopify.dev/docs/api/storefront/latest/objects/CartDiscountCode
   */
  function evaluateProbeSuccess(cart, displayCode) {
    const matching = cart ? findCartDiscountEntry(cart, displayCode) : null;
    if (!matching) return false;
    if (isCampaignDefaultCode(displayCode)) return true;
    if (matching.applicable === false) return false;
    if (matching.applicable === true) return true;

    return (cart?.total_discount || 0) > 0;
  }

  function renderFromCart(cart, urlCode) {
    if (urlCode) {
      restoreValidationFromPersistence(urlCode);
    } else {
      urlCode = rehydratePromoFromPersistence();
    }

    const presentation = resolvePromoPresentation(cart, urlCode);

    if (!presentation.show) {
      hideAll();
      setManualAutoMinimized(false);
      return;
    }

    if (presentation.displayCode) {
      if (
        presentation.state === "stored" ||
        presentation.state === "applied" ||
        presentation.state === "multiple"
      ) {
        persistPromoState(
          presentation.displayCode,
          presentation.state,
          presentation.count,
        );
      }
    }

    const urlContext = hasUrlPromoContext(presentation.displayCode);
    if (!urlContext && isAnnouncementState(presentation.state)) {
      setMinimized(true);
      setManualAutoMinimized(true);
    } else {
      setManualAutoMinimized(false);
    }

    applyPromoResult(
      presentation.displayCode,
      presentation.state,
      isMinimized(),
      presentation.count,
      cart,
    );
    schedulePromoCartSavingsPayloadSync();
    publishState(
      presentation.displayCode,
      presentation.state,
      cart,
      presentation,
    );
  }

  function hideAll() {
    hideInlineFeedback();
    if (triggerEl) triggerEl.hidden = true;
    const currentHost = getPromoHostEl();
    if (currentHost) currentHost.hidden = true;
    if (inlineEl) inlineEl.hidden = true;
    closePopover();
    updateAnnouncementBarLayoutMode();
  }

  function applyPromoResult(displayCode, state, minimized, count = 1, cart = null) {
    const urlContext = hasUrlPromoContext(displayCode);
    const forceInline = shouldForceInlineFeedback();
    const displayState = normalizePromoDisplayState(state, cart, displayCode);
    const announcementVisible = isAnnouncementState(displayState);
    const inlineVisible = isInlineFeedbackState(state, urlContext);

    if (!announcementVisible && !inlineVisible) {
      hideAll();
      return;
    }

    mountAnnouncementElements();
    const currentHost = getPromoHostEl();
    if (currentHost) currentHost.hidden = false;

    if (announcementVisible && !forceInline && triggerEl && currentHost) {
      triggerEl.hidden = false;
      syncPromoCartSavingsPayload();
      populatePromoPopoverWithBreakdown(
        buildPromoDetails(cart, displayCode, displayState, count),
        displayCode,
        displayState,
      );
    } else {
      if (triggerEl) triggerEl.hidden = true;
      closePopover();
    }

    if (inlineVisible && shouldShowInlineFeedback(state, displayCode)) {
      showInlineFeedback(state, displayCode);
    } else if (!inlineVisible) {
      hideInlineFeedback();
    } else if (forceInline && triggerEl) {
      triggerEl.hidden = true;
    }

    updateAnnouncementBarLayoutMode();
  }

  async function fetchCart() {
    if (window.FoxTheme?.cartUi?.fetchCart) {
      return FoxTheme.cartUi.fetchCart();
    }
    const routes = window.FoxTheme?.routes;
    const cartPath = String(routes?.cart_url || "/cart").replace(/\.js$/i, "");
    return fetch(`${cartPath}.js`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      cache: "no-store",
    }).then((r) => r.json());
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function fetchCartWithAttachRetries() {
    const delays = [0, 100, 300, 750, 1500];
    const started = Date.now();
    let cart = null;
    for (let i = 0; i < delays.length; i += 1) {
      const wait = delays[i] - (Date.now() - started);
      if (wait > 0) await sleep(wait);
      cart = await fetchCart().catch(() => null);
      if (pickCodeFromCart(cart)) return cart;
    }
    return cart;
  }

  function publishCartUpdate(cartJson, parsedState) {
    if (probeInFlight) return;

    if (parsedState?.sections) {
      cartJson.sections = parsedState.sections;
    }

    if (FoxTheme?.cartUi?.sync) {
      FoxTheme.cartUi.sync({ cart: cartJson, extra: { source: "promo-url" } });
      return;
    }

    if (FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        cart: cartJson,
      });
    }

    document.documentElement.dispatchEvent(
      new CustomEvent("cart:updated", {
        bubbles: true,
        detail: { cart: cartJson },
      }),
    );

    document.documentElement.dispatchEvent(
      new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: { cart: cartJson },
      }),
    );
  }

  async function applyUrlCodeToCart(urlCode, cart, options = {}) {
    if (!urlCode) return cart || null;
    if (readUserOverride() && !options.campaignDefault) return cart || null;
    if (syncInFlight && !options.probe && !options.exclusive) {
      return cart || null;
    }
    if (syncInFlight && options.exclusive && !options.probe) {
      const started = Date.now();
      while (syncInFlight && Date.now() - started < 2000) {
        await sleep(50);
      }
    }
    if (!window.FoxTheme?.routes?.cart_update_url) return null;

    const trimmed = urlCode.trim();
    const existingCodes = getCartCodeEntries(cart).map((entry) => entry.code);

    if (
      !options.exclusive &&
      existingCodes.some(
        (code) => normalizeCode(code) === normalizeCode(trimmed),
      )
    ) {
      markUrlCodeSeenOnCart();
      return cart || (await fetchCart().catch(() => null));
    }

    syncInFlight = true;

    try {
      const config = FoxTheme.utils.fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];

      const payloadCodes = Array.isArray(options.codes)
        ? [
            ...new Set(
              options.codes
                .map((code) => String(code || "").trim())
                .filter(Boolean),
            ),
          ]
        : options.exclusive
          ? [trimmed]
          : [...existingCodes, trimmed];

      const formData = new FormData();
      formData.append("sections", "");
      formData.append("sections_url", window.location.pathname);
      formData.append("discount", payloadCodes.join(","));

      config.body = formData;

      const response = await fetch(FoxTheme.routes.cart_update_url, config);
      if (!response.ok) throw new Error("Cart discount update failed");

      const parsedState = await response.json();
      const cartJson = await fetchCart();

      if (cartContainsCode(cartJson, trimmed)) {
        markUrlCodeSeenOnCart();
      }

      if (!options.probe && !options.silent) {
        publishCartUpdate(cartJson, parsedState);
      }

      return cartJson;
    } catch (error) {
      console.warn("Promo activation: cart sync failed", error);
      if (options.probe) {
        throw createProbeNetworkError("Probe cart sync failed");
      }
      return null;
    } finally {
      syncInFlight = false;
    }
  }

  async function restoreCartCodes(codes) {
    if (!window.FoxTheme?.routes?.cart_update_url) return null;

    const config = FoxTheme.utils.fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData();
    formData.append("sections", "");
    formData.append("sections_url", window.location.pathname);
    formData.append("discount", codes.join(","));
    config.body = formData;

    try {
      const response = await fetch(FoxTheme.routes.cart_update_url, config);
      if (!response.ok) return null;
      return fetchCart().catch(() => null);
    } catch {
      return null;
    }
  }

  async function enforceStoredCodeAfterCartQualifies(cart, urlCode) {
    if (!cart || !urlCode || readUserOverride()) return cart;
    if (isCampaignDefaultCode(urlCode) || getRealCartLineCount(cart) === 0) {
      return cart;
    }

    const confirmedOwnership = hasConfirmedInfluencerOwnership(urlCode);
    const candidate = findCartDiscountEntry(cart, urlCode);
    const glouraLeftover = cartHasCampaignDefaultCode(cart);
    if (candidate?.applicable === true && !glouraLeftover) return cart;
    if (confirmedOwnership && glouraLeftover) {
      return (
        (await applyUrlCodeToCart(urlCode, cart, {
          exclusive: true,
          codes: listNonDefaultCartCodes(cart, urlCode),
        })) || cart
      );
    }

    const cartShape = (cart.items || [])
      .filter((item) => !isProbeLineItem(item))
      .map((item) => `${item.variant_id || item.id}:${item.quantity}`)
      .sort()
      .join("|");
    const attemptKey = `${normalizeCode(urlCode)}:${cartShape}:${
      confirmedOwnership ? "owned" : "qualify"
    }`;
    if (preferredCodeAttempts.has(attemptKey)) return cart;
    preferredCodeAttempts.add(attemptKey);

    const previousCodes = getCartCodeEntries(cart).map((entry) => entry.code);
    const candidateCart = await applyUrlCodeToCart(urlCode, cart, {
      exclusive: true,
      silent: true,
    });
    const confirmed = findCartDiscountEntry(candidateCart, urlCode);

    if (confirmed?.applicable === true || (confirmedOwnership && confirmed)) {
      writeValidationResult(urlCode, "activated");
      markProbeDone(urlCode);
      persistPromoState(urlCode, "stored");
      publishCartUpdate(candidateCart, null);
      return candidateCart;
    }

    if (confirmedOwnership) {
      if (candidateCart) {
        publishCartUpdate(candidateCart, null);
        return candidateCart;
      }
      return cart;
    }

    const restoredCart = (await restoreCartCodes(previousCodes)) || cart;
    publishCartUpdate(restoredCart, null);
    return restoredCart;
  }

  async function addProbeLine(variantId) {
    const addUrl = window.FoxTheme?.routes?.cart_add_url || "/cart/add.js";

    let response;
    try {
      response = await fetch(addUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                [PROBE_LINE_PROPERTY]: "1",
              },
            },
          ],
        }),
      });
    } catch {
      throw createProbeNetworkError("Probe line add failed");
    }

    if (!response.ok) {
      throw createProbeNetworkError("Probe line add failed");
    }

    const data = await response.json();
    const lineKeys = (data.items || []).map((item) => item.key).filter(Boolean);

    if (!lineKeys.length) {
      throw createProbeNetworkError(
        "Probe line key missing from cart add response",
      );
    }

    rememberProbeVariantId(variantId);
    return lineKeys;
  }

  async function removeProbeLinesByKeys(lineKeys, cart) {
    const changeUrl =
      window.FoxTheme?.routes?.cart_change_url || "/cart/change.js";
    const keysToRemove = new Set();

    (lineKeys || []).forEach((key) => {
      if (key) keysToRemove.add(key);
    });

    (cart?.items || [])
      .filter((item) => isProbeLineItem(item))
      .forEach((item) => {
        if (item.key) keysToRemove.add(item.key);
      });

    for (const lineKey of keysToRemove) {
      try {
        await fetch(changeUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: 0,
          }),
        });
      } catch {
        /* continue */
      }
    }
  }

  async function publishCleanCartRefresh() {
    const cleanCart = await fetchCart().catch(() => null);
    if (!cleanCart) return null;

    publishCartUpdate(cleanCart, null);
    return cleanCart;
  }

  async function runEmptyCartProbe(displayCode) {
    if (probeInFlight) {
      return readValidationResult(displayCode) || "pending";
    }

    const cachedValidation = readValidationResult(displayCode);
    if (isProbeDone(displayCode) && cachedValidation) {
      markFreshUrlDiscountLanding(displayCode);
      applyPromoResult(displayCode, cachedValidation, isMinimized());
      return cachedValidation;
    }

    const variantId = await resolveProbeVariantId();
    if (!variantId) {
      writeValidationResult(displayCode, "pending");
      applyPromoResult(displayCode, "pending", isMinimized());
      return "pending";
    }

    probeInFlight = true;
    markFreshUrlDiscountLanding(displayCode);
    applyPromoResult(displayCode, "checking", isMinimized());

    let cart = null;
    let probeLineKeys = [];
    let result = "pending";

    try {
      probeLineKeys = await addProbeLine(variantId);
      cart = await applyUrlCodeToCart(displayCode, cart, { probe: true });
      if (!cart) {
        throw createProbeNetworkError("Probe cart sync returned empty cart");
      }

      const success = evaluateProbeSuccess(cart, displayCode);
      result = success ? "activated" : "rejected";
      writeValidationResult(displayCode, result);
      markProbeDone(displayCode);
      if (result === "activated") {
        persistPromoState(displayCode, "stored");
      } else if (result === "rejected") {
        clearPersistedPromoState();
      }
    } catch (error) {
      console.warn("Promo activation: probe failed", error);

      if (isProbeNetworkError(error)) {
        result = "pending";
        writeValidationResult(displayCode, "pending");
      } else {
        result = "rejected";
        writeValidationResult(displayCode, "rejected");
        markProbeDone(displayCode);
        clearPersistedPromoState();
      }
    } finally {
      try {
        cart = await fetchCart().catch(() => null);
        await removeProbeLinesByKeys(probeLineKeys, cart);
        clearProbeVariantId();
        cart = await ensureCartWithoutProbeLines(cart);
      } catch {
        /* ignore */
      }

      const urlCode = readStoredCode() || displayCode;
      applyPromoResult(displayCode, result, isMinimized(), 1, cart);
      publishState(displayCode, result, cart, { count: 1 });

      probeInFlight = false;

      const cleanCart = await publishCleanCartRefresh();
      renderFromCart(cleanCart, urlCode);
    }

    return result;
  }

  function extractCodeFromReferrer() {
    try {
      if (!document.referrer) return null;
      const refUrl = new URL(document.referrer);
      return extractFromPath(refUrl.pathname);
    } catch {
      return null;
    }
  }

  function pickCodeFromCart(cart) {
    const discountCodes = cart?.discount_codes || [];
    if (!discountCodes.length) return null;

    const refCode = extractCodeFromReferrer();
    if (refCode) {
      const matched = discountCodes.find(
        (dc) => dc.code && normalizeCode(dc.code) === normalizeCode(refCode),
      );
      if (matched?.code) return matched.code.trim();
      return refCode.trim();
    }

    const influencer = pickAttachedInfluencerCode(cart);
    if (influencer) return influencer;

    if (discountCodes.length === 1 && discountCodes[0].code) {
      return discountCodes[0].code.trim();
    }

    return null;
  }

  function reconcileFromCart(cart) {
    if (readStoredCode()) return null;

    const code = pickCodeFromCart(cart);
    if (!code) return null;

    storeCode(code);
    return { code, source: "cart_reconcile" };
  }

  function shouldReconcileFromCartOnLoad(cart) {
    if (!pickCodeFromCart(cart)) return false;

    if (getRealCartLineCount(cart) === 0) {
      return true;
    }

    try {
      return /\/discount\//i.test(document.referrer || "");
    } catch {
      return false;
    }
  }

  function publishState(displayCode, state, cart, presentation) {
    const urlCode = readStoredCode();

    window.glouraPromoActivation = {
      code: displayCode,
      urlCode,
      state,
      normalized: normalizeCode(displayCode),
      minimized: isMinimized(),
      validation: urlCode ? readValidationResult(urlCode) : null,
      userOverride: readUserOverride(),
      count: presentation?.count || 1,
    };

    document.dispatchEvent(
      new CustomEvent("gloura:promo-state", {
        detail: {
          code: displayCode,
          urlCode,
          state,
          cart,
          minimized: isMinimized(),
          validation: urlCode ? readValidationResult(urlCode) : null,
          userOverride: readUserOverride(),
          count: presentation?.count || 1,
        },
      }),
    );
  }

  function publishUserOverride(reason) {
    document.dispatchEvent(
      new CustomEvent("gloura:promo-user-override", {
        detail: { reason, urlCode: readStoredCode() },
      }),
    );
  }

  function handleUserOverrideSideEffects(cart, urlCode, event) {
    if (readUserOverride()) {
      clearPersistedPromoState();
      const reason = readUserOverride();
      publishUserOverride(reason);
      if (reason === "removed") {
        hideAll();
        return true;
      }
      return false;
    }

    if (!consumeExplicitCartDiscountAction(event)) {
      return false;
    }

    if (!cart || !detectUserOverrideFromCart(cart, urlCode)) {
      return false;
    }

    clearPersistedPromoState();
    const reason = readUserOverride();
    publishUserOverride(reason);

    if (reason === "removed") {
      hideAll();
      return true;
    }

    return false;
  }

  async function refreshUI(cartFromEvent) {
    let urlCode = readStoredCode();
    if (!urlCode) {
      urlCode = rehydratePromoFromPersistence();
    } else {
      restoreValidationFromPersistence(urlCode);
    }

    let cart = cartFromEvent || (await fetchCart().catch(() => null));
    if (cartHasProbeLines(cart)) {
      cart = await ensureCartWithoutProbeLines(cart);
    }

    urlCode = adoptAttachedInfluencerCode(cart, urlCode);

    if (urlCode && handleUserOverrideSideEffects(cart, urlCode)) {
      if (readUserOverride() === "removed") {
        await ensureCampaignDefaultOnEligibleCart(cart);
      }
      return;
    }

    if (urlCode) {
      cart = await enforceStoredCodeAfterCartQualifies(cart, urlCode);
    }

    // Gate (and others) may write gloura:promo-code then fire bare cart:refresh
    // before/after init. Mirror handleCartEvent so the probe still runs.
    if (
      urlCode &&
      getRealCartLineCount(cart) === 0 &&
      (await shouldRunProbe(cart, urlCode))
    ) {
      await runEmptyCartProbe(urlCode);
      return;
    }

    if (urlCode && tryPresentCartActivationOnly(cart, urlCode)) {
      return;
    }

    if (
      !hasActiveInfluencerSession(urlCode, cart) &&
      (!urlCode ||
        isCampaignDefaultCode(urlCode) ||
        readUserOverride() === "removed")
    ) {
      cart = await ensureCampaignDefaultOnEligibleCart(cart);
    }

    renderFromCart(cart, urlCode || null);
  }

  async function shouldSyncUrlCode(urlCode, cart) {
    const normalized = normalizeCode(urlCode);
    if (!normalized) return false;

    return !cartContainsCode(cart, urlCode);
  }

  async function init() {
    mountAnnouncementElements();
    bindAnnouncementObserver();

    let initialActivation = captureUrlCode();
    const redirect = captureRedirectTarget();

    if (redirect) {
      storeRedirect(appendPreviewParams(redirect));
    }

    if (initialActivation?.code) {
      const previous = readStoredCode();
      if (
        !previous ||
        normalizeCode(previous) !== normalizeCode(initialActivation.code)
      ) {
        clearUrlActivationSession();
        clearProbeSessionForOtherCodes(initialActivation.code);
        if (
          previous &&
          normalizeCode(previous) !== normalizeCode(initialActivation.code)
        ) {
          clearPersistedPromoState();
        }
      }

      storeCode(initialActivation.code);
      markFreshUrlDiscountLanding(initialActivation.code);
      clearUserOverride();
      clearMinimizedOnFreshCapture(initialActivation.source);
    }

    let urlCode = readStoredCode();

    if (!urlCode) {
      urlCode = rehydratePromoFromPersistence();
    } else {
      restoreValidationFromPersistence(urlCode);
    }

    if (!urlCode) {
      // After /discount/… redirects, cart.js may lag. Retry briefly before giving up.
      const cart = isDiscountReferrer()
        ? await fetchCartWithAttachRetries()
        : await fetchCart().catch(() => null);
      if (shouldReconcileFromCartOnLoad(cart)) {
        const reconciled = reconcileFromCart(cart);
        if (reconciled) {
          urlCode = reconciled.code;
          initialActivation = reconciled;
          markFreshUrlDiscountLanding(reconciled.code);
          clearMinimizedOnFreshCapture("cart_reconcile");
        }
      }
    }

    if (urlCode && isDiscountReferrer()) {
      markFreshUrlDiscountLanding(urlCode);
    }

    if (!urlCode) {
      let cartOnly = await fetchCart().catch(() => null);
      if (cartHasProbeLines(cartOnly)) {
        cartOnly = await ensureCartWithoutProbeLines(cartOnly);
      }
      cartOnly = await ensureCampaignDefaultOnEligibleCart(cartOnly);
      renderFromCart(cartOnly, null);
      return;
    }

    let cart = await fetchCart().catch(() => null);
    if (cartHasProbeLines(cart)) {
      cart = await ensureCartWithoutProbeLines(cart);
    }

    urlCode = adoptAttachedInfluencerCode(cart, urlCode);
    reassertOwnedCodeIfGlouraReappears().catch(() => {});

    if (handleUserOverrideSideEffects(cart, urlCode)) {
      if (readUserOverride() === "removed") {
        await ensureCampaignDefaultOnEligibleCart(cart);
      }
      return;
    }

    cart = await enforceStoredCodeAfterCartQualifies(cart, urlCode);

    if (tryPresentCartActivationOnly(cart, urlCode)) {
      writeLastCartCodes(cart);
      return;
    }

    if (await shouldRunProbe(cart, urlCode)) {
      await runEmptyCartProbe(urlCode);
      cart = await fetchCart().catch(() => null);
      writeLastCartCodes(cart);
      renderFromCart(cart, urlCode);
      return;
    }

    const cachedValidation = readValidationResult(urlCode);
    if (
      isProbeDone(urlCode) &&
      cachedValidation &&
      (cachedValidation === "activated" || cachedValidation === "rejected") &&
      shouldForceInlineFeedback()
    ) {
      presentUrlValidationResult(urlCode, cachedValidation, cart);
      writeLastCartCodes(cart);
      return;
    }

    if (isInitialActivation(initialActivation) && !readUserOverride()) {
      if (await shouldSyncUrlCode(urlCode, cart)) {
        cart = (await applyUrlCodeToCart(urlCode, cart)) || cart;
      }
    }

    if (tryPresentCartActivationOnly(cart, urlCode)) {
      writeLastCartCodes(cart);
      return;
    }

    if (isCampaignDefaultCode(urlCode)) {
      cart = await ensureCampaignDefaultOnEligibleCart(cart);
    }

    renderFromCart(cart, urlCode);
  }

  async function handleCartEvent(event) {
    if (probeInFlight) return;

    schedulePromoCartSavingsPayloadSync();

    let cart = event?.cart || event?.detail?.cart;

    if (cart && cartHasProbeLines(cart)) {
      cart = await ensureCartWithoutProbeLines(cart);
    }

    if (cart) {
      const claimed = claimVerifiedCartFormCode(cart, event);
      let urlCode = claimed || adoptAttachedInfluencerCode(cart, readStoredCode());

      if (!urlCode) {
        if (shouldReconcileFromCartOnLoad(cart)) {
          const reconciled = reconcileFromCart(cart);
          if (reconciled) {
            markFreshUrlDiscountLanding(reconciled.code);
            clearMinimizedOnFreshCapture("cart_reconcile");
            if (tryPresentCartActivationOnly(cart, reconciled.code)) {
              return;
            }
            if (await shouldRunProbe(cart, reconciled.code)) {
              await runEmptyCartProbe(reconciled.code);
            } else if (isInitialActivation(reconciled)) {
              await applyUrlCodeToCart(reconciled.code, cart);
            }
            let freshCart = await fetchCart().catch(() => null);
            if (isCampaignDefaultCode(reconciled.code)) {
              freshCart = await ensureCampaignDefaultOnEligibleCart(
                freshCart || cart,
                event,
              );
            }
            renderFromCart(freshCart, reconciled.code);
            return;
          }
        }
        cart = await ensureCampaignDefaultOnEligibleCart(cart, event);
        renderFromCart(cart, readStoredCode() || null);
        return;
      }

      if (!claimed && handleUserOverrideSideEffects(cart, urlCode, event)) {
        if (readUserOverride() === "removed") {
          await ensureCampaignDefaultOnEligibleCart(cart, event);
        }
        return;
      }

      if (isCampaignDefaultCode(urlCode)) {
        cart = await ensureCampaignDefaultOnEligibleCart(cart, event);
        renderFromCart(cart, urlCode);
        return;
      }

      cart = await enforceStoredCodeAfterCartQualifies(cart, urlCode);

      if (
        getRealCartLineCount(cart) === 0 &&
        (await shouldRunProbe(cart, urlCode))
      ) {
        await runEmptyCartProbe(urlCode);
        return;
      }

      if (tryPresentCartActivationOnly(cart, urlCode)) {
        return;
      }

      renderFromCart(cart, urlCode);
      return;
    }

    await refreshUI();
  }

  async function waitForDiscountWriteIdle(timeoutMs) {
    const started = Date.now();
    while (syncInFlight && Date.now() - started < timeoutMs) {
      await sleep(50);
    }
    return !syncInFlight;
  }

  function cartHasVerifiedOwnedCode(cart, urlCode) {
    const owned = findCartDiscountEntry(cart, urlCode);
    return Boolean(owned?.applicable === true && !cartHasCampaignDefaultCode(cart));
  }

  async function ensureOwnedCodeBeforeCheckout() {
    if (readUserOverride()) return { ready: true, code: "" };

    const started = Date.now();
    const remaining = () =>
      Math.max(0, CHECKOUT_OWNED_CODE_TIMEOUT_MS - (Date.now() - started));

    if (!(await waitForDiscountWriteIdle(remaining()))) {
      return { ready: false, code: readStoredCode() || "" };
    }

    const cart = await fetchCart().catch(() => null);
    if (!cart || remaining() <= 0) {
      return { ready: false, code: readStoredCode() || "" };
    }

    const selected = pickCheckoutOwnedCode(cart);
    if (!selected) return { ready: true, code: "" };

    let updated = await applyUrlCodeToCart(selected, cart, {
      exclusive: true,
      silent: true,
      codes: listNonDefaultCartCodes(cart, selected),
    });
    if (!(await waitForDiscountWriteIdle(remaining()))) {
      return { ready: false, code: selected };
    }

    let verified = await fetchCart().catch(() => null);
    if (!cartHasVerifiedOwnedCode(verified, selected) && remaining() > 0) {
      updated = await applyUrlCodeToCart(selected, updated || cart, {
        exclusive: true,
        silent: true,
        codes: [selected],
      });
      if (!(await waitForDiscountWriteIdle(remaining()))) {
        return { ready: false, code: selected };
      }
      verified = await fetchCart().catch(() => null);
    }

    return {
      ready: cartHasVerifiedOwnedCode(verified, selected),
      code: selected,
    };
  }

  function checkoutDiscountRedirect(code) {
    const normalized = normalizeCode(code);
    if (!normalized || isCampaignDefaultCode(normalized)) return "";
    return `/discount/${encodeURIComponent(normalized)}?redirect=${encodeURIComponent("/checkout")}`;
  }

  function resolveLiveCheckoutForm(trigger) {
    const fromTrigger =
      trigger instanceof HTMLFormElement
        ? trigger
        : trigger?.form || trigger?.closest?.("form");
    if (fromTrigger instanceof HTMLFormElement && document.contains(fromTrigger)) {
      return fromTrigger;
    }

    return (
      document.querySelector("#CartDrawer form") ||
      document.querySelector('form[action*="/cart"]') ||
      null
    );
  }

  async function resumeCheckoutAfterScrub(trigger) {
    if (probeInFlight || probeScrubInFlight) return false;

    let cart = await fetchCart().catch(() => null);
    if (cartHasProbeLines(cart)) {
      cart = await ensureCartWithoutProbeLines(cart);
    }

    const recheck = await fetchCart().catch(() => null);
    if (cartHasProbeLines(recheck)) {
      console.warn(
        "Promo activation: checkout blocked because probe line remained in cart",
      );
      return false;
    }

    const form = resolveLiveCheckoutForm(trigger);
    const selected = pickCheckoutOwnedCode(recheck || cart);
    const ownedCheckoutCode =
      selected && !isCampaignDefaultCode(selected) ? selected : "";
    const nativeCheckoutUrl = checkoutDiscountRedirect(ownedCheckoutCode);

    // A confirmed customer-selected code must enter checkout through Shopify's
    // native discount route. Ajax cart.js can look correct while hosted
    // checkout still reapplies GLOURA onto a reused checkout token.
    if (nativeCheckoutUrl) {
      window.location.assign(nativeCheckoutUrl);
      return true;
    }

    if (form) {
      if (!form.querySelector('[name="checkout"]')) {
        const checkoutField = document.createElement("input");
        checkoutField.type = "hidden";
        checkoutField.name = "checkout";
        checkoutField.value = "";
        form.appendChild(checkoutField);
      }

      if (ownedCheckoutCode) {
        let discountField = form.querySelector(
          'input[name="discount"][data-gloura-owned-code]',
        );
        if (!discountField) {
          discountField = document.createElement("input");
          discountField.type = "hidden";
          discountField.name = "discount";
          discountField.setAttribute("data-gloura-owned-code", "1");
          form.appendChild(discountField);
        }
        discountField.value = ownedCheckoutCode;
      } else {
        form
          .querySelector('input[name="discount"][data-gloura-owned-code]')
          ?.remove();
      }

      HTMLFormElement.prototype.submit.call(form);
      return true;
    }

    if (ownedCheckoutCode) {
      window.location.assign(
        `/checkout?discount=${encodeURIComponent(ownedCheckoutCode)}`,
      );
    } else {
      window.location.assign("/checkout");
    }
    return true;
  }

  let checkoutWaitInFlight = false;
  let checkoutWaitTrigger = null;

  function setCheckoutControlBusy(trigger, busy) {
    if (!(trigger instanceof Element)) return;
    try {
      if (busy) {
        trigger.setAttribute("aria-busy", "true");
      } else {
        trigger.removeAttribute("aria-busy");
      }
    } catch (e) {}
  }

  function setCheckoutIntentState(trigger, state) {
    if (!(trigger instanceof Element)) return;
    try {
      if (state) {
        trigger.setAttribute(CHECKOUT_INTENT_STATE_ATTR, state);
      } else {
        trigger.removeAttribute(CHECKOUT_INTENT_STATE_ATTR);
      }
    } catch (e) {}
  }

  function clearCheckoutWaitState() {
    setCheckoutControlBusy(checkoutWaitTrigger, false);
    checkoutWaitTrigger = null;
    checkoutWaitInFlight = false;
  }

  function notifyCheckoutProbeSoftWait() {
    const message =
      "Dein Rabattcode wird noch geprüft. Du wirst automatisch zur Kasse weitergeleitet.";
    try {
      if (inlineMessageEl && inlineEl) {
        showInlineFeedback("checking", readStoredCode() || "");
        inlineMessageEl.textContent = message;
      }
    } catch (e) {}
  }

  function notifyCheckoutProbeHardTimeout() {
    const message =
      'Die Prüfung dauert länger als erwartet. Bitte klicke erneut auf „Zur Kasse“.';
    try {
      if (inlineMessageEl && inlineEl) {
        showInlineFeedback("checking", readStoredCode() || "");
        inlineMessageEl.textContent = message;
        return;
      }
    } catch (e) {}
    try {
      window.alert(message);
    } catch (e) {}
  }

  function notifyCheckoutAttributionRetry() {
    const message =
      'Dein Influencer-Code wird noch übernommen. Bitte klicke erneut auf „Zur Kasse“.';
    try {
      if (inlineMessageEl && inlineEl) {
        showInlineFeedback("checking", readStoredCode() || "");
        inlineMessageEl.textContent = message;
        return;
      }
    } catch (e) {}
    try {
      window.alert(message);
    } catch (e) {}
  }

  function handleCheckoutIntent(event) {
    const trigger =
      event.target instanceof Element
        ? event.target.closest('button[name="checkout"], input[name="checkout"]')
        : null;
    // Non-checkout clicks must never be intercepted (menu, password eye, links, etc.).
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();

    // Single-flight: further clicks while waiting must not start another continuation.
    if (checkoutWaitInFlight) return;

    checkoutWaitInFlight = true;
    checkoutWaitTrigger = trigger;
    finishInlineFeedback();
    setCheckoutIntentState(trigger, "hold");
    setCheckoutControlBusy(trigger, true);

    (async () => {
      const startedAt = Date.now();
      const softWaitMs = 15000;
      const hardWaitMs = 60000;
      let notifiedSoftTimeout = false;

      try {
        while (probeInFlight || probeScrubInFlight) {
          const waited = Date.now() - startedAt;
          if (waited > hardWaitMs) {
            // Do not call resumeCheckoutAfterScrub while probe is still active —
            // that would silently consume the prevented click.
            setCheckoutIntentState(trigger, "retry");
            notifyCheckoutProbeHardTimeout();
            return;
          }
          if (!notifiedSoftTimeout && waited > softWaitMs) {
            notifiedSoftTimeout = true;
            notifyCheckoutProbeSoftWait();
          }
          await new Promise((resolve) => setTimeout(resolve, 50));
        }

        const ownership = await Promise.race([
          ensureOwnedCodeBeforeCheckout(),
          sleep(CHECKOUT_OWNED_CODE_TIMEOUT_MS).then(() => ({
            ready: false,
            code: readStoredCode() || "",
          })),
        ]);
        const selectedCode = ownership.code || "";
        const nativeUrl = checkoutDiscountRedirect(selectedCode);

        // Keep hold until navigation starts. Setting ready here races the
        // header 400ms fallback, which then sends the shopper to bare /checkout
        // and drops the selected influencer code.
        if (nativeUrl) {
          window.location.assign(nativeUrl);
          return;
        }

        const resumed = await resumeCheckoutAfterScrub(trigger);
        if (!resumed) {
          notifyCheckoutProbeHardTimeout();
        }
      } finally {
        clearCheckoutWaitState();
      }
    })().catch(() => {
      clearCheckoutWaitState();
    });
  }

  inlineDismissEl?.addEventListener("click", () => {
    finishInlineFeedback();
  });

  triggerEl?.addEventListener("click", () => {
    if (!popoverEl || triggerEl.hidden) return;
    if (popoverEl.hidden) {
      const urlCode = readStoredCode();
      fetchCart()
        .then((cart) => {
          syncPromoCartSavingsPayload();
          const presentation = resolvePromoPresentation(cart, urlCode);
          if (!presentation.show) return;

          const displayState = normalizePromoDisplayState(
            presentation.state,
            cart,
            presentation.displayCode,
          );
          populatePromoPopoverWithBreakdown(
            buildPromoDetails(
              cart,
              presentation.displayCode,
              displayState,
              presentation.count,
            ),
            presentation.displayCode,
            displayState,
          );
          openPopover();
        })
        .catch(() => {
          openPopover();
        });
    } else {
      closePopover();
    }
  });

  popoverDismissEl?.addEventListener("click", () => {
    closePopover();
  });

  document.addEventListener("click", (event) => {
    if (!popoverEl || popoverEl.hidden) return;
    const target = event.target;
    if (!(target instanceof Node)) return;

    if (popoverEl.contains(target) || triggerEl?.contains(target)) return;
    closePopover();
  });

  window.addEventListener(
    "resize",
    () => {
      positionPopover();
    },
    { passive: true },
  );
  document.addEventListener("cart:updated", handleCartEvent);
  document.documentElement.addEventListener("cart:updated", handleCartEvent);
  document.addEventListener("cart:refresh", handleCartEvent);

  if (FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
    FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      handleCartEvent,
    );
  }

  bindExplicitCartDiscountTracking();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("click", handleCheckoutIntent, true);
})();
